import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { UserSelector } from '@/components/shared/UserSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface IncidentCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  preLinkedEntity?: SelectedEntity;
}

// Exclude 'incident' from allowed types (can't link incident to itself)
const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'incident');

export function IncidentCreateDialog({ open, onOpenChange, onSave, preLinkedEntity }: IncidentCreateDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    impact: '',
    status: 'draft',
    owner_id: undefined as string | undefined,
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  // Pre-populate links when entity is provided
  useEffect(() => {
    if (preLinkedEntity && open) {
      setSelectedLinks([preLinkedEntity]);
    } else if (!open) {
      setSelectedLinks([]);
    }
  }, [preLinkedEntity, open]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({ title: '', priority: 'medium', impact: '', status: 'draft', owner_id: undefined });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report Incident</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Incident title"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="reported">Reported</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="impact">Impact</Label>
            <Textarea
              id="impact"
              value={formData.impact}
              onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
              placeholder="Describe the impact"
            />
          </div>

          <UserSelector
            value={formData.owner_id}
            onChange={(userId) => setFormData({ ...formData, owner_id: userId })}
            label="Incident Owner / Assignee"
            placeholder="Select owner"
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.title}>
            Report Incident
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
